/**
 * FLASK RENDERER — draws Erlenmeyer Neon flask and physics bodies. Pure render; no physics.
 */

import * as C from "../config/constants.js";
import { getPhysicsMouthGateTopY } from "./system/geometry.js";
import { gameImageUrl } from "../assets/game-image-urls.js";
import { getCachedImage } from "../assets/image-cache.js";
import { runtimeFlags } from "../core/runtime-flags.js";
import { MergeAssets } from "../assets/merge-assets.js";
import { getJarBodyDrawer } from "../render/toon/jar/jar-bodies.js";
import { getJarMark, ringFrameSize } from "../render/toon/jar/jar-marks.js";
import {
  drawFlaskShardBursts,
  getFlaskDamageSeverity,
} from "./flask-damage-shards.js";
import {
  computeFallFlaskOutlineGeom,
  FALL_FLASK_COLLIDERS,
  getFallFlaskColliderTuning,
  getFlaskImageAspectRatio,
  letterboxFlaskInRect,
} from "../run/fall-flask-shape.js";

var DEFAULT_COLORS = {
  flaskWall: "#ffffff",
  flaskWallHighlight: "#ffffff",
  flaskFill: "rgba(255, 255, 255, 0.08)",
  mergeText: "#1B5E7D",
};

var FLASK_SKIN_URLS = {
  full: "flask",
  crackOverlay: "flaskCrack",
  crackOverlay2: "flaskCrack2",
  shard1: "flaskShard1",
  shard2: "flaskShard2",
};

/**
 * Per-hit crack art (Fall only): full-frame PNGs aligned with flask.png (same 256×383 canvas).
 * HP 2 → crack1; HP 1 → crack1 + crack2 stacked. Not drawn in Merge/Shake.
 */
var FLASK_CRACK_LAYERS = [
  { key: "crackOverlay", opacity: 1 },
  { key: "crackOverlay2", opacity: 1 },
];

function getFlaskSkinImage(key) {
  var urlKey = FLASK_SKIN_URLS[key];
  if (!urlKey) return null;
  return getCachedImage(gameImageUrl(urlKey));
}

function getColors(opts) {
  var tierColors = (opts && opts.tierColors) || C.MERGE_PICKUP_COLORS || [];
  var c = Object.assign({}, DEFAULT_COLORS);
  var tierCount = (opts && opts.evolutionChain && opts.evolutionChain.length) || 15;
  for (var i = 0; i < tierCount; i++) c["tier" + i] = tierColors[i] || "#94a3b8";
  return c;
}

// HP level → damage shard intensity (0 = none)
function getCrackSeverity(flaskHp) {
  return getFlaskDamageSeverity(flaskHp);
}

function getCrackLevel(flaskHp) {
  var s = getCrackSeverity(flaskHp);
  if (s === 0) return flaskHp <= 0 ? "none" : "full";
  if (s >= 2) return "crack2";
  return "crack1";
}

function getFlaskShardImage(variant) {
  return getFlaskSkinImage(variant === 1 ? "shard2" : "shard1");
}

/**
 * Letterbox a flask-aspect image into a rect (matches flask.png / crack PNGs).
 * @returns {{ drawX: number, drawY: number, drawW: number, drawH: number }|null}
 */
function letterboxFlaskLayout(x, y, w, h, img) {
  if (!(w > 0) || !(h > 0)) return null;
  var aspect = img && img.naturalWidth > 0
    ? img.naturalHeight / img.naturalWidth
    : 383 / 256;
  var drawW = w;
  var drawH = w * aspect;
  if (drawH > h) {
    drawH = h;
    drawW = h / aspect;
  }
  return {
    drawX: x + (w - drawW) / 2,
    drawY: y + (h - drawH) / 2,
    drawW: drawW,
    drawH: drawH,
  };
}

function drawSingleCrackOverlay(ctx, layout, layer) {
  var img = getFlaskSkinImage(layer.key);
  if (!img || !layout) return false;
  var opacity = layer.opacity != null && isFinite(layer.opacity) ? layer.opacity : 1;

  ctx.save();
  var parentAlpha = ctx.globalAlpha != null && isFinite(ctx.globalAlpha) ? ctx.globalAlpha : 1;
  ctx.globalAlpha = parentAlpha * opacity;
  ctx.imageSmoothingEnabled = true;
  if (ctx.imageSmoothingQuality != null) ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    img,
    0,
    0,
    img.naturalWidth,
    img.naturalHeight,
    layout.drawX,
    layout.drawY,
    layout.drawW,
    layout.drawH
  );
  ctx.restore();
  return true;
}

/**
 * Stack crack overlays for each boulder hit (crack1, then crack1+crack2).
 * @param {typeof FLASK_CRACK_LAYERS} [layers]
 */
function drawFlaskCrackOverlays(ctx, layout, severity, layers) {
  if (!severity || !layout) return false;
  var layerSet = layers || FLASK_CRACK_LAYERS;
  var drewAny = false;
  var count = Math.min(severity, layerSet.length);
  for (var i = 0; i < count; i++) {
    if (drawSingleCrackOverlay(ctx, layout, layerSet[i])) drewAny = true;
  }
  return drewAny;
}

function drawFlaskCrackAtLayout(ctx, layout, flaskHp) {
  if (!layout || getCrackSeverity(flaskHp) <= 0) return false;
  return drawFlaskCrackOverlays(ctx, layout, getCrackSeverity(flaskHp), FLASK_CRACK_LAYERS);
}

/**
 * Draw a flask skin PNG scaled to fit rect (letterboxed).
 * Returns layout when an image was drawn.
 */
function drawFlaskSkinInRect(ctx, x, y, w, h) {
  var img = getFlaskSkinImage("full");
  if (!img) return false;
  var layout = letterboxFlaskLayout(x, y, w, h, img);
  if (!layout) return false;

  ctx.save();
  ctx.imageSmoothingEnabled = true;
  if (ctx.imageSmoothingQuality != null) ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, layout.drawX, layout.drawY, layout.drawW, layout.drawH);
  ctx.restore();
  return { drew: true, drawX: layout.drawX, drawY: layout.drawY, drawW: layout.drawW, drawH: layout.drawH };
}

function drawHpCracksOnGeom(ctx, geom, crackLevel, scale) {
  if (!geom || crackLevel === "none" || crackLevel === "full") return;

  var bodyTop = geom.bodyTop;
  var bodyBottom = geom.bodyBottom;
  var floorLeft = geom.floorLeft != null ? geom.floorLeft : geom.cx - geom.floorHalfWidth;
  var floorRight = geom.floorRight != null ? geom.floorRight : geom.cx + geom.floorHalfWidth;

  ctx.save();
  ctx.strokeStyle = "rgba(239,68,68,0.75)";
  ctx.lineWidth = 2 * scale;
  ctx.lineCap = "round";
  var bH = bodyBottom - bodyTop;
  var bW = floorRight - floorLeft;
  var bCX = (floorLeft + floorRight) / 2;
  ctx.beginPath();
  ctx.moveTo(bCX - bW * 0.08, bodyTop + bH * 0.28);
  ctx.lineTo(bCX - bW * 0.04, bodyTop + bH * 0.48);
  ctx.lineTo(bCX - bW * 0.18, bodyTop + bH * 0.68);
  ctx.stroke();
  if (crackLevel === "crack2") {
    ctx.beginPath();
    ctx.moveTo(bCX + bW * 0.12, bodyTop + bH * 0.38);
    ctx.lineTo(bCX + bW * 0.08, bodyTop + bH * 0.58);
    ctx.lineTo(bCX + bW * 0.22, bodyTop + bH * 0.74);
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Vector fallback when PNG skins are not loaded yet.
 */
function drawErlenmeyerOnCtx(ctx, geom, flaskHp, scale) {
  if (!geom) return;
  scale = scale || 1;
  var crackLevel = getCrackLevel(flaskHp);
  if (crackLevel === "none") return;

  var neckLeft = geom.neckLeft;
  var neckRight = geom.neckRight;
  var neckBottom = geom.neckBottom;
  var bodyTop = geom.bodyTop;
  var bodyBottom = geom.bodyBottom;
  var floorLeft = geom.floorLeft != null ? geom.floorLeft : geom.cx - geom.floorHalfWidth;
  var floorRight = geom.floorRight != null ? geom.floorRight : geom.cx + geom.floorHalfWidth;
  var T = geom.T;

  ctx.save();

  ctx.shadowColor = "rgba(31, 118, 160, 0.45)";
  ctx.shadowBlur = 18 * scale;
  ctx.strokeStyle = crackLevel === "crack2" ? "#ef4444" : crackLevel === "crack1" ? "#f97316" : "#ffffff";
  ctx.lineWidth = 3 * scale;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  ctx.beginPath();
  ctx.moveTo(neckLeft, T);
  ctx.lineTo(neckRight, T);
  ctx.lineTo(neckRight, neckBottom);
  ctx.lineTo(floorRight, bodyBottom);
  ctx.lineTo(floorLeft, bodyBottom);
  ctx.lineTo(neckLeft, neckBottom);
  ctx.lineTo(neckLeft, T);
  ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.beginPath();
  ctx.moveTo(neckLeft, T);
  ctx.lineTo(neckRight, T);
  ctx.lineTo(neckRight, neckBottom);
  ctx.lineTo(floorRight, bodyBottom);
  ctx.lineTo(floorLeft, bodyBottom);
  ctx.lineTo(neckLeft, neckBottom);
  ctx.closePath();
  ctx.fillStyle = "rgba(255, 255, 255, 0.1)";
  ctx.fill();

  ctx.strokeStyle = "rgba(255,255,255,0.28)";
  ctx.lineWidth = 2 * scale;
  ctx.lineCap = "round";
  ctx.beginPath();
  var innerOffsetX = (neckRight - neckLeft) * 0.22;
  var bodyRefX1 = neckLeft + innerOffsetX;
  var bodyRefX2 = floorLeft + (floorRight - floorLeft) * 0.1;
  ctx.moveTo(bodyRefX1, T + 4 * scale);
  ctx.lineTo(bodyRefX1, bodyTop);
  ctx.lineTo(bodyRefX2, bodyBottom - 10 * scale);
  ctx.stroke();

  ctx.strokeStyle = "rgba(255,255,255,0.55)";
  ctx.lineWidth = 2 * scale;
  ctx.beginPath();
  ctx.moveTo(neckLeft + 2 * scale, T);
  ctx.lineTo(neckRight - 2 * scale, T);
  ctx.stroke();

  drawHpCracksOnGeom(ctx, geom, crackLevel, scale);

  ctx.restore();
}

/**
 * Compute Erlenmeyer geometry from a placement rect (letterboxed like `flask.png`).
 * Matches Stage I `FallPhysics` / `run/fall-flask-shape.js`.
 */
function erlenmeyerGeomFromRect(x, y, w, h, runConfig) {
  var cx = x + w / 2;
  var draw = letterboxFlaskInRect(x, y, w, h, getFlaskImageAspectRatio());
  var shape = runConfig ? getFallFlaskColliderTuning(runConfig) : FALL_FLASK_COLLIDERS;
  return computeFallFlaskOutlineGeom(cx, draw, shape);
}

function geomToDrawRect(geom) {
  if (!geom) return null;
  var floorLeft = geom.floorLeft != null ? geom.floorLeft : geom.cx - geom.floorHalfWidth;
  var floorRight = geom.floorRight != null ? geom.floorRight : geom.cx + geom.floorHalfWidth;
  var floorHalfW = (floorRight - floorLeft) / 2;
  var w = floorHalfW / 0.44;
  var x = geom.cx - w / 2;
  var y = geom.T;
  var h = geom.bodyBottom - y;
  return { x: x, y: y, w: w, h: h };
}

/**
 * Image sprites for a flask rect (skin + crack overlays). Empty when PNGs are not loaded.
 */
function collectFlaskVisualSprites(x, y, w, h, flaskHp, keyPrefix) {
  if (flaskHp <= 0) return { specs: [], layout: null };
  var img = getFlaskSkinImage("full");
  if (!img) return { specs: [], layout: null };
  var layout = letterboxFlaskLayout(x, y, w, h, img);
  if (!layout) return { specs: [], layout: null };
  var prefix = keyPrefix || "flask";
  var cx = layout.drawX + layout.drawW / 2;
  var cy = layout.drawY + layout.drawH / 2;
  var specs = [{
    key: prefix + "-skin",
    image: img,
    x: cx,
    y: cy,
    width: layout.drawW,
    height: layout.drawH,
    anchorX: 0.5,
    anchorY: 0.5,
    zIndex: 10,
  }];
  var severity = getCrackSeverity(flaskHp);
  var count = Math.min(severity, FLASK_CRACK_LAYERS.length);
  for (var i = 0; i < count; i++) {
    var layer = FLASK_CRACK_LAYERS[i];
    var cimg = getFlaskSkinImage(layer.key);
    if (!cimg) continue;
    var opacity = layer.opacity != null && isFinite(layer.opacity) ? layer.opacity : 1;
    specs.push({
      key: prefix + "-crack-" + i,
      image: cimg,
      x: cx,
      y: cy,
      width: layout.drawW,
      height: layout.drawH,
      alpha: opacity,
      anchorX: 0.5,
      anchorY: 0.5,
      zIndex: 11 + i,
    });
  }
  return { specs: specs, layout: layout };
}

function drawFlaskVisual(ctx, x, y, w, h, flaskHp) {
  if (flaskHp <= 0) return false;

  var crackSeverity = getCrackSeverity(flaskHp);
  var crackLevel = getCrackLevel(flaskHp);
  var geom = erlenmeyerGeomFromRect(x, y, w, h);
  var scale = Math.min(w, h) / 80;
  var skinLayout = drawFlaskSkinInRect(ctx, x, y, w, h);
  if (skinLayout && skinLayout.drew) {
    if (crackSeverity > 0) {
      if (!drawFlaskCrackOverlays(ctx, skinLayout, crackSeverity)) {
        drawHpCracksOnGeom(ctx, geom, crackLevel, scale);
      }
    }
    return true;
  }
  drawErlenmeyerOnCtx(ctx, geom, flaskHp, scale);
  return true;
}

/**
 * Draw the Erlenmeyer flask at an explicit rect (used in run/fall stage runway icon).
 */
function drawFlaskAtRect(ctx, x, y, w, h, flaskHp) {
  return drawFlaskVisual(ctx, x, y, w, h, flaskHp);
}

/**
 * Draw the Erlenmeyer flask using the physics geometry object.
 */
function drawFlaskImage(ctx, geom, flaskHp) {
  var rect = geomToDrawRect(geom);
  if (!rect) return false;
  return drawFlaskVisual(ctx, rect.x, rect.y, rect.w, rect.h, flaskHp);
}

function drawFlaskShardBurstsForRenderer(ctx, bursts, nowMs, drawOpts) {
  return drawFlaskShardBursts(ctx, bursts, nowMs, getFlaskShardImage, drawOpts);
}

/**
 * Draw the Erlenmeyer flask physics outline (debug / test mode).
 * Includes invisible Shake headroom above the visual rim when `physicsWallExtendUpFrac` is set.
 */
function drawFlaskPhysicsOutline(ctx, geom) {
  if (!geom) return;
  var neckLeft = geom.neckLeft;
  var neckRight = geom.neckRight;
  var bodyTop = geom.bodyTop;
  var bodyBottom = geom.bodyBottom;
  var floorLeft = geom.floorLeft != null ? geom.floorLeft : geom.cx - geom.floorHalfWidth;
  var floorRight = geom.floorRight != null ? geom.floorRight : geom.cx + geom.floorHalfWidth;
  var T = geom.T;
  var gateTopY = getPhysicsMouthGateTopY(geom);
  var hasPhysicsHeadroom = gateTopY < T - 0.5;

  ctx.save();
  ctx.lineWidth = 2;

  if (hasPhysicsHeadroom) {
    ctx.strokeStyle = "rgba(192, 38, 211, 0.95)";
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(neckLeft, gateTopY);
    ctx.lineTo(neckRight, gateTopY);
    ctx.lineTo(neckRight, T);
    ctx.lineTo(neckLeft, T);
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "rgba(192, 38, 211, 0.95)";
    ctx.font = "bold 10px monospace";
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";
    ctx.fillText("physics headroom", neckLeft + 2, gateTopY - 2);
  }

  ctx.strokeStyle = "#00aaff";
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(neckLeft, hasPhysicsHeadroom ? T : gateTopY);
  ctx.lineTo(neckRight, hasPhysicsHeadroom ? T : gateTopY);
  ctx.lineTo(neckRight, bodyTop);
  ctx.lineTo(floorRight, bodyBottom);
  ctx.lineTo(floorLeft, bodyBottom);
  ctx.lineTo(neckLeft, bodyTop);
  ctx.closePath();
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
}

function drawMouthGateLine(ctx, mouthSealBody) {
  if (!mouthSealBody || !mouthSealBody.bounds || !mouthSealBody.position) return;
  var minX = mouthSealBody.bounds.min.x;
  var maxX = mouthSealBody.bounds.max.x;
  var y = mouthSealBody.bounds.min.y;
  ctx.save();
  ctx.strokeStyle = "#f43f5e";
  ctx.lineWidth = 3;
  ctx.setLineDash([8, 6]);
  ctx.beginPath();
  ctx.moveTo(minX, y);
  ctx.lineTo(maxX, y);
  ctx.stroke();
  var midX = (minX + maxX) / 2;
  var arrowH = 10;
  ctx.setLineDash([]);
  ctx.fillStyle = "#f43f5e";
  ctx.beginPath();
  ctx.moveTo(midX - 5, y - 2);
  ctx.lineTo(midX + 5, y - 2);
  ctx.lineTo(midX, y + arrowH);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

// The merge flash, merge particle burst and drop splash that used to be drawn here are now
// cartoon blasts and landing puffs from the juice layer (render/juice/game-juice.js), driven by
// `flask:merge` and drop landings, so chain merges no longer cut each other off.

/**
 * Shrink ghost: an inked dashed ring at the body's old size (render/toon/jar/jar-marks.js),
 * turning slowly like marching ants while the creature shrinks inside it.
 */
function drawShrinkOutline(ctx, x, y, radiusX, radiusY, angle, alpha, colors, playfield, origin, key) {
  if (!(alpha > 0.01)) return;
  var rMax = Math.max(radiusX, radiusY);
  var ghost = getJarMark("ghost", null, rMax);
  var spin = (angle || 0) + (Date.now() % 100000) * 0.0012;
  if (ghost) {
    var fw = ringFrameSize(rMax);
    var gw = fw * radiusX / rMax;
    var gh = fw * radiusY / rMax;
    if (playfield && typeof playfield.effect === "function") {
      origin = origin || { x: 0, y: 0 };
      playfield.effect({
        key: key || "shrink",
        kind: "texture",
        image: ghost,
        x: origin.x + x,
        y: origin.y + y,
        width: gw,
        height: gh,
        rotation: spin,
        alpha: Math.min(1, alpha * 1.25),
        zIndex: 32,
      });
      return;
    }
    if (ctx && typeof ctx.drawImage === "function") {
      ctx.save();
      ctx.globalAlpha = Math.min(1, alpha * 1.25);
      ctx.translate(x, y);
      ctx.rotate(spin);
      ctx.drawImage(ghost, -gw / 2, -gh / 2, gw, gh);
      ctx.restore();
      return;
    }
  }
  if (playfield && typeof playfield.effect === "function") {
    origin = origin || { x: 0, y: 0 };
    playfield.effect({
      key: key || "shrink",
      kind: "ellipse",
      x: origin.x + x,
      y: origin.y + y,
      radiusX: radiusX,
      radiusY: radiusY,
      rotation: angle || 0,
      rgb: [255, 255, 255],
      alpha: alpha,
      stroke: 2.25,
      zIndex: 32,
    });
    return;
  }
  if (!ctx) return;
  var stroke = colors && colors.shrinkOutline ? colors.shrinkOutline : "rgba(255, 255, 255, 0.92)";
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 2.25;
  ctx.setLineDash([5, 4]);
  ctx.lineDashOffset = -Date.now() * 0.02;
  ctx.beginPath();
  if (Math.abs(radiusX - radiusY) > 0.01 && typeof ctx.ellipse === "function") {
    ctx.ellipse(x, y, radiusX, radiusY, angle || 0, 0, Math.PI * 2);
  } else {
    ctx.arc(x, y, Math.max(radiusX, radiusY), 0, Math.PI * 2);
  }
  ctx.stroke();
  ctx.restore();
}

/**
 * Draw flask and all bodies.
 */
function draw(ctx, opts) {
  var bounds = opts.bounds || { left: 20, right: 300, top: 70, bottom: 465 };
  var bodies = opts.bodies || [];
  var bodyRadiusByTier = opts.bodyRadiusByTier || [6, 8, 10, 12, 14, 16, 18, 20];
  var evolutionChain = opts.evolutionChain || [];
  var mergeMotionDraw = opts.mergeMotionDraw || null;
  var shrinkMotionDraw = opts.shrinkMotionDraw || null;
  var colors = getColors(opts);
  var flaskGeometry = opts.flaskGeometry;
  var mouthSealBody = opts.mouthSealBody;

  var playfield = opts.playfield;
  var origin = opts.worldOrigin || { x: 0, y: 0 };

  if ((runtimeFlags.showFlaskPhysicsOutline || runtimeFlags.showLayoutSectionBorders) && flaskGeometry) {
    // Pixi path: ctx stays in screen space (bodies go to the playfield at `origin`), so shift the
    // outline into jar space. The canvas path's ctx is already translated there.
    ctx.save();
    if (playfield) ctx.translate(origin.x, origin.y);
    drawFlaskPhysicsOutline(ctx, flaskGeometry);
    drawMouthGateLine(ctx, mouthSealBody);
    ctx.restore();
  }

  // Layered cartoon bodies with drawn-only squash / blink / pop (render/toon/jar/jar-bodies.js).
  // The merge tween's grow + fade is replaced by its squash-stretch pose there.
  var bodyDrawer = opts.jarBodies === false ? null : getJarBodyDrawer();
  if (bodyDrawer) {
    bodyDrawer.begin({
      animTime: opts.animTime,
      unitRadius: bodyRadiusByTier[0],
      mergeMotions: opts.activeMergeMotions,
      juice: opts.juice,
    });
  }

  for (var i = 0; i < bodies.length; i++) {
    var meta = bodies[i];
    var pos = meta.body.position;
    var tier = meta.tier;
    var radius = bodyRadiusByTier[tier] != null ? bodyRadiusByTier[tier] : 10;
    var radiusX = meta.radiusX != null ? meta.radiusX : radius;
    var radiusY = meta.radiusY != null ? meta.radiusY : radius;
    var motion = mergeMotionDraw && mergeMotionDraw[meta.body.id];
    var shrink = shrinkMotionDraw && shrinkMotionDraw[meta.body.id];
    if (shrink) {
      radiusX = shrink.drawRadiusX != null ? shrink.drawRadiusX : radiusX;
      radiusY = shrink.drawRadiusY != null ? shrink.drawRadiusY : radiusY;
    } else if (motion && !bodyDrawer) {
      if (motion.scale != null && isFinite(motion.scale)) {
        radiusX *= motion.scale;
        radiusY *= motion.scale;
      }
    }
    var fallbackColor = colors["tier" + tier] != null ? colors["tier" + tier] : colors.tier0;
    var cfg = evolutionChain[tier] || {};
    var ellipseAngle = cfg.ellipseAngle != null ? cfg.ellipseAngle : 0;
    var physicsAngle = meta.body.angle + ellipseAngle;
    if (shrink) {
      drawShrinkOutline(
        ctx,
        pos.x,
        pos.y,
        shrink.outlineRadiusX,
        shrink.outlineRadiusY,
        physicsAngle,
        shrink.outlineAlpha,
        colors,
        playfield,
        origin,
        "shrink-" + meta.body.id
      );
    }
    var drewImage = false;
    var pose = bodyDrawer ? bodyDrawer.pose(meta, radiusX, radiusY, motion, !!shrink) : null;
    if (pose) {
      drewImage = playfield && typeof playfield.sprite === "function"
        ? bodyDrawer.publish(playfield, origin, meta, i, radiusX, radiusY, pose)
        : bodyDrawer.drawCanvas(ctx, meta, radiusX, radiusY, pose);
    } else if (playfield && MergeAssets && MergeAssets.layout) {
      var layout = MergeAssets.layout(pos.x, pos.y, radiusX, tier, meta.body.angle, radiusY);
      if (layout) {
        var bodyAlpha = motion && motion.alpha != null ? motion.alpha : 1;
        playfield.sprite({
          key: "merge-" + meta.body.id,
          image: layout.image,
          x: origin.x + layout.x,
          y: origin.y + layout.y,
          width: layout.width,
          height: layout.height,
          rotation: layout.rotation,
          alpha: bodyAlpha,
          anchorX: layout.anchorX,
          anchorY: layout.anchorY,
          zIndex: 20,
        });
        drewImage = true;
      }
    }
    if (drewImage && runtimeFlags.showPhysicsBodyContour) {
      ctx.save();
      // Pixi hands over the untranslated overlay context: place the contour in board space.
      if (playfield) ctx.translate(origin.x, origin.y);
      ctx.strokeStyle = runtimeFlags.testFlaskMode ? "#00aaff" : "rgba(255,255,255,0.6)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      if (Math.abs(radiusX - radiusY) > 0.01 && typeof ctx.ellipse === "function") {
        ctx.ellipse(pos.x, pos.y, radiusX, radiusY, physicsAngle, 0, Math.PI * 2);
      } else {
        ctx.arc(pos.x, pos.y, Math.max(radiusX, radiusY), 0, Math.PI * 2);
      }
      ctx.stroke();
      ctx.restore();
    }
    ctx.save();
    if (motion && motion.alpha != null) ctx.globalAlpha = motion.alpha;
    if (!drewImage && MergeAssets && MergeAssets.draw) {
      drewImage = MergeAssets.draw(
        ctx,
        pos.x,
        pos.y,
        radiusX,
        tier,
        fallbackColor,
        meta.body.angle,
        radiusY
      );
    } else if (!drewImage) {
      ctx.fillStyle = fallbackColor;
      ctx.beginPath();
      if (Math.abs(radiusX - radiusY) > 0.01) {
        ctx.ellipse(pos.x, pos.y, radiusX, radiusY, physicsAngle, 0, Math.PI * 2);
      } else {
        ctx.arc(pos.x, pos.y, radiusX, 0, Math.PI * 2);
      }
      ctx.fill();
    }
    if (!drewImage) {
      ctx.fillStyle = colors.mergeText;
      ctx.font = "10px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      var label = evolutionChain[tier] ? evolutionChain[tier].label : "?";
      ctx.fillText(label, pos.x, pos.y);
    }
    ctx.restore();
  }
  if (bodyDrawer) bodyDrawer.end();
}

export const FlaskRenderer = {
  draw: draw,
  drawFlaskAtRect: drawFlaskAtRect,
  collectFlaskVisualSprites: collectFlaskVisualSprites,
  drawFlaskShardBursts: drawFlaskShardBurstsForRenderer,
  drawFlaskPhysicsOutline: drawFlaskPhysicsOutline,
  drawMouthGateLine: drawMouthGateLine,
};